// System directives

"use client";

// System dependencies

import React from "react";
import { useController, Control, FieldValues, Path } from "react-hook-form";

// Application dependencies

import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormInputCase } from "@/interfaces/formInput.interfaces";
import { Input } from "@/components/ui/input";
import { formatTitleCase } from "@/lib/functions";

// Props

interface FormInputTextProps<T extends FieldValues> {
  control: Control<T>;
  inputCase?: FormInputCase;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  label: string;
  name: Path<T>;
  placeholder?: string;
  type?: string;
}

const FormInputText = <T extends FieldValues>({
  control,
  inputCase = FormInputCase.None,
  name,
  label,
  placeholder,
  type = "text",
  inputProps,
}: FormInputTextProps<T>) => {
  // Spread out the field and field state.

  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  // Handle input transformation on blur

  const handleOnBlur = () => {
    if (inputCase === FormInputCase.None) {
      return; // No transformation required
    }

    let shapedValue = field.value as string;

    switch (inputCase) {
      case FormInputCase.Lowercase:
        shapedValue = shapedValue.toLowerCase();
        break;
      case FormInputCase.Uppercase:
        shapedValue = shapedValue.toUpperCase();
        break;
      case FormInputCase.Titlecase:
        shapedValue = shapedValue.toLowerCase().replace(/\b\w/g, (char: string) => char.toUpperCase());
        break;
      case FormInputCase.Namecase:
        shapedValue = formatTitleCase(shapedValue);
        break;
    }

    field.onChange(shapedValue); // Update the field value
  };

  // Handle "Enter" keypress to trigger blur behavior

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleOnBlur();
    }
  };

  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem>
          <FormLabel className="text-base">{label}</FormLabel>
          <FormControl>
            <Input
              {...field}
              {...inputProps}
              className="rounded bg-white !text-base dark:bg-slate-700"
              onBlur={handleOnBlur}
              onKeyDown={handleKeyDown}
              style={{
                textTransform:
                  inputCase === FormInputCase.Uppercase ? "uppercase" : inputCase === FormInputCase.Lowercase ? "lowercase" : "none",
              }}
              placeholder={placeholder}
              type={type}
            />
          </FormControl>
          {!error && <FormDescription className="text-base">{placeholder}</FormDescription>}
          <FormMessage className="text-base" />
        </FormItem>
      )}
    />
  );
};

export default FormInputText;
